import { useState } from "react";
import { DOT, LABEL, TEXT_ACTION, choice } from "./styles";

type SnippetLang = "cli" | "curl" | "python";

const TABS: Array<{ id: SnippetLang; label: string }> = [
  { id: "cli", label: "CLI" },
  { id: "curl", label: "curl" },
  { id: "python", label: "Python" },
];

const jsonString = (value: string): string => JSON.stringify(value);

/** Build the equivalent API call for the crafted command. `command` is the
 * verb (search/grep/map/filter) and `raw` is everything after it — exactly
 * what POST /api/cli/execute takes. */
const buildSnippet = (
  lang: SnippetLang,
  display: string,
  command: string,
  raw: string,
  origin: string,
): string => {
  // No `$ ` prompt here — it's rendered as a separate, non-selectable span so
  // selecting the snippet copies a line that runs verbatim in a real shell.
  if (lang === "cli") return `paperclip ${display}`;
  if (lang === "curl") {
    return [
      `curl -X POST ${origin}/api/cli/execute \\`,
      `  -H "Authorization: Bearer $PAPERCLIP_TOKEN" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -d '${JSON.stringify({ command, raw })}'`,
    ].join("\n");
  }
  return [
    `import requests`,
    ``,
    `r = requests.post(`,
    `    "${origin}/api/cli/execute",`,
    `    headers={"Authorization": f"Bearer {token}"},`,
    `    json={"command": ${jsonString(command)},`,
    `          "raw": ${jsonString(raw)}},`,
    `)`,
    `print(r.json()["output"])`,
  ].join("\n");
};

/** Live command mirroring the current form, with the equivalent API call in
 * curl/Python — the same request the Run button sends. */
export const CommandPreview = ({
  command,
  cliCommand,
  cliRaw,
}: {
  /** Display string without the `paperclip` prefix. */
  command: string;
  /** Verb + raw args for the API snippets (fall back to display parsing). */
  cliCommand?: string;
  cliRaw?: string;
}) => {
  const [lang, setLang] = useState<SnippetLang>("cli");
  const [copied, setCopied] = useState(false);

  const verb = cliCommand ?? command.split(/\s+/, 1)[0] ?? "";
  const raw = cliRaw ?? command.slice(verb.length).trim();
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://gxl.ai";
  const snippet = buildSnippet(lang, command, verb, raw, origin);

  const handleCopy = () => {
    // The CLI tab copies a runnable shell line (no `$ ` prompt).
    navigator.clipboard.writeText(
      lang === "cli" ? `paperclip ${command}` : snippet,
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-4">
        <span className={LABEL}>Command</span>
        <div role="tablist" aria-label="Snippet language" className="flex items-baseline gap-x-3">
          {TABS.map((tab, i) => (
            <span key={tab.id} className="flex items-baseline gap-x-3">
              {i > 0 && <span aria-hidden className={DOT}>·</span>}
              <button
                type="button"
                role="tab"
                aria-selected={lang === tab.id}
                onClick={() => setLang(tab.id)}
                className={choice(lang === tab.id).replace("text-base leading-6", "text-sm leading-5")}
              >
                {tab.label}
              </button>
            </span>
          ))}
        </div>
        <button type="button" onClick={handleCopy} className={`${TEXT_ACTION} ml-auto`}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="pg-code mt-1.5 max-h-56 overflow-auto whitespace-pre-wrap break-all px-4 py-3 font-mono text-xs leading-6 text-zinc-700">
        <code>
          {lang === "cli" && <span className="select-none text-zinc-400">$ </span>}
          {snippet}
        </code>
      </pre>
    </div>
  );
};
